// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Bulk & Packaging Page
// Tank-to-tank transfers, dip stick adjustments, bulk-to-pack drum runs
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/Tabs';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Plus, Search, Package, ArrowRightLeft, Calculator } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';

interface BulkPackRun {
  _id: string;
  runNumber: string;
  batchRecordId: string;
  orderId: string;
  sourceTankId: string;
  sourceBatchNumber: string;
  productId: string;
  packagingType: string;
  plannedQty: number;
  actualQtyPacked: number;
  unitsProduced: number;
  unitTareWeight: number;
  unitNetWeight: number;
  unitGrossWeight: number;
  packagingLossQty: number;
  packagingLossPct: number;
  totalPackagingCost: number;
  status: string;
  createdAt: string;
}

interface TankTransfer {
  _id: string;
  transferNumber: string;
  sourceTankId: string;
  targetTankId: string;
  sourceBatchNumber: string;
  sourceProductId: string;
  observedVolumeTransferred: number;
  standardVolumeTransferred: number;
  standardMassTransferred: number;
  vcfSource: number;
  vcfTarget: number;
  dipAdjustmentVariance: number;
  massVariance: number;
  transferMethod: string;
  startTime: string;
  endTime: string;
  status: string;
}

export function BulkPackagingPage() {
  const [packRuns, setPackRuns] = useState<BulkPackRun[]>([]);
  const [transfers, setTransfers] = useState<TankTransfer[]>([]);
  const [activeTab, setActiveTab] = useState<'packaging' | 'transfers'>('packaging');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const { get, post } = useApi();
  
  useEffect(() => {
    fetchData();
  }, [activeTab, get]);
  
  async function fetchData() {
    setLoading(true);
    try {
      if (activeTab === 'packaging') {
        const res = await get('/api/manufacturing/packaging/runs');
        setPackRuns(res.runs || []);
      } else {
        const res = await get('/api/manufacturing/transfers');
        setTransfers(res.transfers || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }
  
  function handleOpenCreate() {
    if (activeTab === 'packaging') {
      setFormData({
        runNumber: '',
        batchRecordId: '',
        orderId: '',
        sourceTankId: '',
        sourceBatchNumber: '',
        productId: '',
        packagingType: 'drum_200l',
        plannedQty: 0,
        unitTareWeight: 18,
        unitNetWeight: 200,
        packagingMaterialCost: 0,
        packagingLaborCost: 0,
        packagingOverheadCost: 0,
        status: 'draft',
      });
    } else {
      setFormData({
        transferNumber: '',
        sourceTankId: '',
        targetTankId: '',
        sourcePlantId: '',
        targetPlantId: '',
        sourceBatchNumber: '',
        sourceProductId: '',
        observedVolumeTransferred: 0,
        sourceApiGravity: 0,
        sourceTemp: 15,
        targetTemp: 15,
        transferMethod: 'pump',
        startTime: new Date().toISOString().slice(0, 16),
        endTime: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
        initiatedBy: 'current-user',
        status: 'draft',
      });
    }
    setShowDialog(true);
  }
  
  // Both a pack run and a tank transfer move real tank mass/volume the
  // instant they're created (see the backend's POST handlers in
  // src/api-manufacturing/index.js) — there's no draft/posted distinction
  // honoured server-side despite the 'draft' status default above, so
  // editing or deleting the record afterward would desync it from the tank
  // levels it already changed with no way to reverse that. Neither
  // operation exists on the backend for this reason; this only ever
  // creates a new run/transfer.
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (activeTab === 'packaging') {
        await post('/api/manufacturing/packaging/bulk-to-pack', formData);
      } else {
        await post('/api/manufacturing/transfers/tank-to-tank', formData);
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save:', error);
    }
  }

  const filteredPackRuns = packRuns.filter(p => 
    p.runNumber.toLowerCase().includes(search.toLowerCase()) ||
    p.sourceBatchNumber.toLowerCase().includes(search.toLowerCase())
  ).filter(p => !filterStatus || p.status === filterStatus);
  
  const filteredTransfers = transfers.filter(t => 
    t.transferNumber.toLowerCase().includes(search.toLowerCase()) ||
    t.sourceBatchNumber.toLowerCase().includes(search.toLowerCase())
  ).filter(t => !filterStatus || t.status === filterStatus);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bulk & Packaging</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Tank-to-tank transfers, dip stick VCF adjustments, bulk-to-pack drum runs</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="w-4 h-4 mr-2" />
          {activeTab === 'packaging' ? 'New Pack Run' : 'New Transfer'}
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="packaging"><Package className="w-4 h-4 mr-2" /> Bulk-to-Pack</TabsTrigger>
          <TabsTrigger value="transfers"><ArrowRightLeft className="w-4 h-4 mr-2" /> Tank Transfers</TabsTrigger>
        </TabsList>
        
        <TabsContent value="packaging" className="space-y-4">
          <div className="flex gap-4">
            <Input placeholder="Search pack runs..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="released">Released</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {loading ? (
            <Card className="p-8 text-center">Loading...</Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run #</TableHead>
                    <TableHead>Batch #</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Packaging</TableHead>
                    <TableHead>Planned (kg)</TableHead>
                    <TableHead>Actual (kg)</TableHead>
                    <TableHead>Units</TableHead>
                    <TableHead>Loss %</TableHead>
                    <TableHead>Packaging Cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPackRuns.map(run => (
                    <TableRow key={run._id}>
                      <TableCell className="font-mono font-medium">{run.runNumber}</TableCell>
                      <TableCell className="font-mono text-sm">{run.sourceBatchNumber}</TableCell>
                      <TableCell>{run.productId}</TableCell>
                      <TableCell><Badge variant="secondary">{run.packagingType.replace('_', ' ')}</Badge></TableCell>
                      <TableCell>{run.plannedQty.toLocaleString()}</TableCell>
                      <TableCell>{run.actualQtyPacked.toLocaleString()}</TableCell>
                      <TableCell>{run.unitsProduced}</TableCell>
                      <TableCell className={run.packagingLossPct > 1 ? 'text-red-600' : 'text-green-600'}>{run.packagingLossPct.toFixed(2)}%</TableCell>
                      <TableCell className="font-mono">{(run.totalPackagingCost / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell><Badge variant={run.status === 'completed' || run.status === 'closed' ? 'success' : run.status === 'draft' ? 'secondary' : 'outline'}>{run.status}</Badge></TableCell>
                      <TableCell>
                        {/* No edit/delete: creating a run already moved real
                            tank mass, and there's no supported way to
                            reverse that — see handleSubmit's comment. */}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredPackRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-gray-500">No pack runs found. Click "New Pack Run" to create one.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="transfers" className="space-y-4">
          <div className="flex gap-4">
            <Input placeholder="Search transfers..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="reconciled">Reconciled</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {loading ? (
            <Card className="p-8 text-center">Loading...</Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transfer #</TableHead>
                    <TableHead>Source Tank</TableHead>
                    <TableHead>Target Tank</TableHead>
                    <TableHead>Batch #</TableHead>
                    <TableHead>Obs. Volume (L)</TableHead>
                    <TableHead>Std Volume (L)</TableHead>
                    <TableHead>Std Mass (kg)</TableHead>
                    <TableHead>VCF Source</TableHead>
                    <TableHead>VCF Target</TableHead>
                    <TableHead>Dip Variance</TableHead>
                    <TableHead>Mass Variance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransfers.map(transfer => (
                    <TableRow key={transfer._id}>
                      <TableCell className="font-mono font-medium">{transfer.transferNumber}</TableCell>
                      <TableCell className="font-mono text-sm">{transfer.sourceTankId}</TableCell>
                      <TableCell className="font-mono text-sm">{transfer.targetTankId}</TableCell>
                      <TableCell className="font-mono text-sm">{transfer.sourceBatchNumber}</TableCell>
                      <TableCell>{transfer.observedVolumeTransferred.toLocaleString()}</TableCell>
                      <TableCell>{transfer.standardVolumeTransferred.toLocaleString()}</TableCell>
                      <TableCell>{transfer.standardMassTransferred.toLocaleString()}</TableCell>
                      <TableCell>{transfer.vcfSource.toFixed(6)}</TableCell>
                      <TableCell>{transfer.vcfTarget.toFixed(6)}</TableCell>
                      <TableCell className={transfer.dipAdjustmentVariance > 100 ? 'text-red-600' : 'text-green-600'}>{transfer.dipAdjustmentVariance.toLocaleString()}</TableCell>
                      <TableCell className={transfer.massVariance > 10 ? 'text-red-600' : 'text-green-600'}>{transfer.massVariance.toFixed(2)}</TableCell>
                      <TableCell><Badge variant={transfer.status === 'completed' || transfer.status === 'reconciled' ? 'success' : transfer.status === 'draft' ? 'secondary' : 'outline'}>{transfer.status}</Badge></TableCell>
                      <TableCell>
                        {/* No edit/delete: creating a transfer already moved
                            real tank mass between tanks, and there's no
                            supported way to reverse that — see
                            handleSubmit's comment. */}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTransfers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-gray-500">No tank transfers found. Click "New Transfer" to create one.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* VCF Calculator Section */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Volume Correction Factor (VCF) Calculator
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Calculate VCF per ASTM D1250 for temperature correction of oil volumes</p>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">API Gravity</label>
              <Input type="number" step="0.1" placeholder="e.g., 35.0" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Observed Temp (°C)</label>
              <Input type="number" step="0.1" placeholder="e.g., 25.0" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reference Temp (°C)</label>
              <Input type="number" step="0.1" placeholder="15.0" defaultValue="15" />
            </div>
            <div className="flex items-end">
              <Button variant="outline"><Calculator className="w-4 h-4 mr-2" /> Calculate VCF</Button>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>VCF Formula:</strong> VCF = exp(-α × (T_obs - T_ref)) where α ≈ 0.000185 + 0.0000074×API + 0.00000018×API² (per °F)
              <br />
              Standard Volume = Observed Volume × VCF
            </p>
          </div>
        </div>
      </Card>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create {activeTab === 'packaging' ? 'Pack Run' : 'Tank Transfer'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="p-4 space-y-4">
              {activeTab === 'packaging' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Run Number *</label>
                      <Input value={formData.runNumber} onChange={e => setFormData({...formData, runNumber: e.target.value.toUpperCase()})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Batch Record ID *</label>
                      <Input value={formData.batchRecordId} onChange={e => setFormData({...formData, batchRecordId: e.target.value})} required placeholder="Batch Record ObjectId" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Order ID *</label>
                      <Input value={formData.orderId} onChange={e => setFormData({...formData, orderId: e.target.value})} required placeholder="Order ObjectId" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Source Tank ID *</label>
                      <Input value={formData.sourceTankId} onChange={e => setFormData({...formData, sourceTankId: e.target.value})} required placeholder="Tank ObjectId" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Source Batch #</label>
                      <Input value={formData.sourceBatchNumber} onChange={e => setFormData({...formData, sourceBatchNumber: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Product ID *</label>
                      <Input value={formData.productId} onChange={e => setFormData({...formData, productId: e.target.value})} required placeholder="Product ObjectId" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Packaging Type *</label>
                      <Select value={formData.packagingType} onValueChange={v => setFormData({...formData, packagingType: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="drum_200l">200L Drum</SelectItem>
                          <SelectItem value="ibc_1000l">1000L IBC</SelectItem>
                          <SelectItem value="tote">Tote</SelectItem>
                          <SelectItem value="bag_25kg">25kg Bag</SelectItem>
                          <SelectItem value="bag_50kg">50kg Bag</SelectItem>
                          <SelectItem value="iso_tank">ISO Tank</SelectItem>
                          <SelectItem value="bulk_tanker">Bulk Tanker</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Planned Qty (kg) *</label>
                      <Input type="number" value={formData.plannedQty} onChange={e => setFormData({...formData, plannedQty: Number(e.target.value)})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Unit Tare Weight (kg)</label>
                      <Input type="number" step="0.1" value={formData.unitTareWeight} onChange={e => setFormData({...formData, unitTareWeight: Number(e.target.value)})} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Unit Net Weight (kg)</label>
                      <Input type="number" step="0.1" value={formData.unitNetWeight} onChange={e => setFormData({...formData, unitNetWeight: Number(e.target.value)})} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="released">Released</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Packaging Material Cost</label>
                      <Input type="number" value={formData.packagingMaterialCost} onChange={e => setFormData({...formData, packagingMaterialCost: Number(e.target.value)})} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Packaging Labor Cost</label>
                      <Input type="number" value={formData.packagingLaborCost} onChange={e => setFormData({...formData, packagingLaborCost: Number(e.target.value)})} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Packaging Overhead Cost</label>
                      <Input type="number" value={formData.packagingOverheadCost} onChange={e => setFormData({...formData, packagingOverheadCost: Number(e.target.value)})} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Transfer Number *</label>
                      <Input value={formData.transferNumber} onChange={e => setFormData({...formData, transferNumber: e.target.value.toUpperCase()})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Source Tank ID *</label>
                      <Input value={formData.sourceTankId} onChange={e => setFormData({...formData, sourceTankId: e.target.value})} required placeholder="Tank ObjectId" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Target Tank ID *</label>
                      <Input value={formData.targetTankId} onChange={e => setFormData({...formData, targetTankId: e.target.value})} required placeholder="Tank ObjectId" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Source Plant ID *</label>
                      <Input value={formData.sourcePlantId} onChange={e => setFormData({...formData, sourcePlantId: e.target.value})} required placeholder="Plant ObjectId" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Target Plant ID *</label>
                      <Input value={formData.targetPlantId} onChange={e => setFormData({...formData, targetPlantId: e.target.value})} required placeholder="Plant ObjectId" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Source Batch #</label>
                      <Input value={formData.sourceBatchNumber} onChange={e => setFormData({...formData, sourceBatchNumber: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Source Product ID *</label>
                      <Input value={formData.sourceProductId} onChange={e => setFormData({...formData, sourceProductId: e.target.value})} required placeholder="Product ObjectId" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Observed Volume (L) *</label>
                      <Input type="number" step="0.1" value={formData.observedVolumeTransferred} onChange={e => setFormData({...formData, observedVolumeTransferred: Number(e.target.value)})} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">API Gravity *</label>
                      <Input type="number" step="0.1" value={formData.sourceApiGravity} onChange={e => setFormData({...formData, sourceApiGravity: Number(e.target.value)})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Source Temp (°C) *</label>
                      <Input type="number" step="0.1" value={formData.sourceTemp} onChange={e => setFormData({...formData, sourceTemp: Number(e.target.value)})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Target Temp (°C) *</label>
                      <Input type="number" step="0.1" value={formData.targetTemp} onChange={e => setFormData({...formData, targetTemp: Number(e.target.value)})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Method</label>
                      <Select value={formData.transferMethod} onValueChange={v => setFormData({...formData, transferMethod: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pump">Pump</SelectItem>
                          <SelectItem value="gravity">Gravity</SelectItem>
                          <SelectItem value="pipeline">Pipeline</SelectItem>
                          <SelectItem value="truck">Truck</SelectItem>
                          <SelectItem value="rail">Rail</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Start Time *</label>
                      <Input type="datetime-local" value={formData.startTime} onChange={e => setFormData({...formData, startTime: e.target.value})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">End Time *</label>
                      <Input type="datetime-local" value={formData.endTime} onChange={e => setFormData({...formData, endTime: e.target.value})} required />
                    </div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}